import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { useTranslations } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { Icons, ic } from "../wizard-icons";
import type { ProductTypeId, SingleOption } from "../wizard-data";

const K = keys.quickSetup;

/**
 * "What do you sell?" — single-select choice-card grid (2×3 desktop / stacked
 * or 2-col mobile). Selecting a card deselects any other. Choosing "other"
 * ("Something else") reveals a required freeform text input. The primary
 * button is always enabled; tapping it validates and shows an inline error
 * instead of staying disabled.
 */
export function SellTypeStep({
  options,
  selected,
  other,
  onSelect,
  onOtherChange,
  onDone,
  narrow,
}: {
  options: SingleOption[];
  selected: ProductTypeId | null;
  other: string;
  onSelect: (id: ProductTypeId) => void;
  onOtherChange: (value: string) => void;
  onDone: () => void;
  narrow: boolean;
}): ReactElement {
  const t = useTranslations();
  const [error, setError] = useState<string | null>(null);
  const otherInputRef = useRef<HTMLInputElement>(null);

  const showOther = selected === "other";

  // Move focus into the freeform field once it has revealed, so the seller can
  // type straight away — but only after the expand transition, not on mount.
  const wasShown = useRef(showOther);
  useEffect(() => {
    if (showOther && !wasShown.current) {
      const id = window.setTimeout(() => otherInputRef.current?.focus(), 240);
      wasShown.current = true;
      return () => window.clearTimeout(id);
    }
    if (!showOther) wasShown.current = false;
  }, [showOther]);

  const handleSelect = (id: ProductTypeId) => {
    setError(null);
    onSelect(id);
  };

  const handleContinue = () => {
    if (!selected) {
      setError(t(K.errors.sellType));
      return;
    }
    if (selected === "other" && !other.trim()) {
      setError(t(K.errors.sellType));
      return;
    }
    setError(null);
    onDone();
  };

  return (
    <div style={{ animation: "qs-in .3s ease" }}>
      <div
        style={{
          display: "grid",
          // One column on mobile so long labels ("Beauty & skincare") show in
          // full without the 2-col grid overflowing the 390px viewport; 2-up on
          // desktop. minmax(0,…) lets tracks shrink instead of forcing overflow.
          gridTemplateColumns: narrow ? "minmax(0, 1fr)" : "minmax(0, 1fr) minmax(0, 1fr)",
          gap: narrow ? 10 : 12,
          marginBottom: 12,
        }}
      >
        {options.map((o) => {
          const on = selected === o.id;
          return (
            <button
              key={o.id}
              onClick={() => handleSelect(o.id)}
              aria-pressed={on}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: on ? "space-between" : "flex-start",
                gap: 12,
                padding: narrow ? "14px" : "16px 18px",
                minHeight: 44,
                borderRadius: 12,
                cursor: "pointer",
                fontFamily: "inherit",
                textAlign: "start",
                transition: "all .15s",
                background: on ? "var(--sel-bg)" : "var(--surface)",
                border: `${on ? 2 : 1.5}px solid ${on ? "var(--accent)" : "var(--border-strong)"}`,
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                <span style={{ display: "flex", color: on ? "var(--gold-ink)" : "var(--text-3)", flexShrink: 0 }}>
                  {ic(o.icon, narrow ? 16 : 17)}
                </span>
                <span
                  style={{
                    fontSize: narrow ? 14 : 15,
                    fontWeight: 700,
                    color: "var(--navy)",
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {o.label}
                </span>
              </span>
              {on && (
                <span
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: narrow ? 18 : 20,
                    height: narrow ? 18 : 20,
                    borderRadius: 999,
                    background: "var(--accent)",
                    color: "var(--navy)",
                    flexShrink: 0,
                  }}
                >
                  {ic(Icons.check, narrow ? 11 : 12, { stroke: 3 })}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Freeform reveal for "Something else": collapses/expands via a
          grid-template-rows 0fr→1fr transition (+ fade) so the Continue button
          is smoothly pushed down instead of the input popping in. */}
      <div
        style={{
          display: "grid",
          gridTemplateRows: showOther ? "1fr" : "0fr",
          opacity: showOther ? 1 : 0,
          transition:
            "grid-template-rows .34s cubic-bezier(.2,.8,.3,1), opacity .26s ease",
        }}
        aria-hidden={!showOther}
      >
        <div style={{ overflow: "hidden", minHeight: 0 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 8, paddingBottom: 20 }}>
            <label style={{ fontSize: 13, fontWeight: 700, color: "var(--navy)" }}>
              {t(K.custom.label)}
            </label>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                border: "2px solid var(--accent)",
                borderRadius: 12,
                padding: `0 ${narrow ? 14 : 18}px`,
                height: narrow ? 50 : 54,
                background: "var(--surface)",
                boxSizing: "border-box",
              }}
            >
              <input
                ref={otherInputRef}
                value={other}
                onChange={(e) => {
                  setError(null);
                  onOtherChange(e.target.value);
                }}
                placeholder={t(K.custom.placeholder)}
                tabIndex={showOther ? 0 : -1}
                style={{
                  flex: 1,
                  minWidth: 0,
                  border: "none",
                  outline: "none",
                  fontSize: narrow ? 15 : 16,
                  fontFamily: "inherit",
                  color: "var(--text)",
                  background: "transparent",
                }}
              />
            </div>
            <span style={{ fontSize: 13, color: "var(--fine)" }}>{t(K.custom.hint)}</span>
          </div>
        </div>
      </div>

      {error && (
        <div style={{ marginBottom: 12, fontSize: 13, fontWeight: 600, color: "var(--danger)" }}>
          {error}
        </div>
      )}

      <button
        onClick={handleContinue}
        style={{
          width: "100%",
          padding: "13px 16px",
          borderRadius: 10,
          border: "none",
          background: "var(--accent)",
          color: "var(--navy)",
          fontSize: 16,
          fontWeight: 800,
          cursor: "pointer",
          fontFamily: "inherit",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
        }}
      >
        {t(K.logo.continue)}
      </button>
    </div>
  );
}
